import { get, merge } from 'lodash-es';
import { authenticatedSaveVinAction } from 'client/engagement-handlers/profile-engagement-handler/profile-engagement-handler';
import { TrackingConstant } from 'client/tracking/constant';
import { getSyncDate } from 'site-modules/shared/components/profile/idm/idm-utils';
import { formatMergedAttributes } from 'site-modules/shared/components/profile/attributes/merged-attributes';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { INVENTORY_TYPES } from 'client/constants/inventory-types';
import { LEAD_PRODUCT_TYPES } from 'site-modules/shared/constants/lead-form/lead-form-params';
import { formatVehiclesVinAttribute } from 'site-modules/shared/components/profile/attributes/vehicles';
import { getVehiclePrices } from 'client/actions/vehicle';
import { getLeadTargetInfo } from 'site-modules/shared/components/lead-form/utils/utils';

const CREATIVE_ID = 'lf-anonymous-new-reg';
const signInAnonymouslyTracking = {
  creativeId: CREATIVE_ID,
  trackingData: {
    subaction_name: TrackingConstant.SUCCESS_EMAIL_ONLY_SIGN_UP,
  },
};
const PHONE_TYPES = ['Mobile', 'LandLine', 'Other'];

function getMergedAttributes(leadTarget, modelLinkCode) {
  const { inventory } = leadTarget;
  const { styleInfo, vehicleColors, partsInfo } = getLeadTargetInfo(leadTarget);
  const type = inventory ? inventory.type : INVENTORY_TYPES.NEW;

  return formatMergedAttributes({
    vehicleInfo: { styleInfo, vehicleColors, partsInfo },
    type,
    modelLinkCode,
  }).mergedAttributes;
}

function getInsiderLeadType({ inventory, leadType }) {
  return (
    leadType ||
    (inventory && isUsed(inventory.type) ? LEAD_PRODUCT_TYPES.usedInventory : LEAD_PRODUCT_TYPES.newInventory)
  );
}

/**
 * Vin Payload. If is dealerlocator lead type then "nonVin" parameter is true.
 * @param {Object} leadTarget
 * @param {Object} vehicleParams
 */
function getVehiclePayload(leadTarget, vehicleParams) {
  const nonVin = leadTarget.inventory ? null : { nonVin: true };

  return merge(formatVehiclesVinAttribute(leadTarget, vehicleParams), nonVin);
}

/**
 * Determine vehicles vins reference for insider payload. All "non-vins" (dealerlocator lead type) should reference a "fake-vin" which will be the leadId.
 * @param {Object} leadTarget
 */
function getVehicleReference({ leadId, inventory }) {
  return `#/vehicles/vins/${inventory ? inventory.vin : leadId}`;
}

function getLeadPayload(leadProductType, leadTarget) {
  const {
    dealerInfo: { franchiseId, rooftopId },
  } = getLeadTargetInfo(leadTarget);

  return {
    type: leadProductType,
    franchiseId,
    rooftopId: `${rooftopId}`,
    vinReference: getVehicleReference(leadTarget),
  };
}

function getPhoneNumbers(pi) {
  const phoneType = PHONE_TYPES.includes(pi.phoneType) ? pi.phoneType : PHONE_TYPES[2];
  return {
    phoneNumbers: [
      {
        phoneType,
        areaCode: pi.phone.substring(0, 3),
        number: `${pi.phone.substring(3, 6)}-${pi.phone.substring(6, 10)}`,
      },
    ],
  };
}

function getContactPayload(pi) {
  return {
    email: pi.email,
    ...getPhoneNumbers(pi),
  };
}

/**
 *
 * @param {Array} leadTargets - array of leads to save to UPS
 * @param {Object} personalInfo - user personal info
 * @param {String} modelLinkCode
 * @param {Boolean} isFullPayload
 * @param {Boolean} isAnonymousUserWithoutEmail
 */
export function createInsiderPayload(
  leadTargets,
  personalInfo,
  modelLinkCode,
  isFullPayload,
  isAnonymousUserWithoutEmail
) {
  const firstLeadTarget = leadTargets[0];

  const dateNow = getSyncDate();
  const pi = personalInfo;
  const mergedAttributes = getMergedAttributes(firstLeadTarget, modelLinkCode);

  // Set up empty object to be appended to later.
  const vehiclePayload = {};
  const leadsPayload = {};
  const contactsPayload = {};
  const leadSentLocations = [];

  leadTargets.forEach(leadTarget => {
    const {
      dealerInfo,
      styleInfo: { trim },
    } = getLeadTargetInfo(leadTarget);
    const leadId = leadTarget.leadId;
    const vehicleRef = leadTarget.inventory ? leadTarget.inventory.vin : leadId;

    const leadProductType = getInsiderLeadType(leadTarget);
    const vehicleParams = leadTarget.vehicleParams;
    const { price, salePrice } = getVehiclePrices(get(leadTarget, 'inventory.prices'));

    // Each lead needs a location ID and Timestamp (for multi-select)
    leadSentLocations.push({
      locationId: parseInt(dealerInfo.franchiseId, 10),
      leadSentTs: dateNow,
    });

    // Append current vin to vehiclePayload
    vehiclePayload[vehicleRef] = getVehiclePayload(leadTarget, vehicleParams);

    // Append current lead to leadsPayload
    leadsPayload[leadId] = {
      ...getLeadPayload(leadProductType, leadTarget),
      submittedTs: dateNow,
    };

    // Append current contact to contactsPayload
    contactsPayload[leadId] = {
      ...getContactPayload(pi),
    };

    authenticatedSaveVinAction(vehicleRef, {
      creative_id: CREATIVE_ID,
      action_cause: 'button_click',
      trim,
      price,
      sale_price: salePrice,
    });
  });

  // Payload for registed users will not overwrite identifiers.email, person.firstname, pii.lastname
  const retObj = {
    intelligentMessaging: {
      leadSentLocations,
    },
    lastLeadReference: `#/leads/${firstLeadTarget.leadId}`, // Since all leads happen at the same time, we pick the first lead as the latest.
    pii: {
      contactInfo: contactsPayload,
    },
    vehicles: {
      vins: vehiclePayload,
    },
    leads: leadsPayload,
    mergedAttributes,
  };

  if (isFullPayload || isAnonymousUserWithoutEmail) {
    merge(retObj, {
      identifiers: {
        email: [pi.email],
      },
      person: {
        firstName: pi.firstName,
      },
      pii: {
        lastName: pi.lastName,
        ...getPhoneNumbers(pi),
      },
    });
  }

  return retObj;
}

function mapLeadTargets(leadTargets, leadIds) {
  return leadTargets.map((leadTarget, index) => ({
    ...leadTarget,
    leadId: leadIds[index],
  }));
}

/**
 * Use existing firebase authentication or create anonymous account, then submit lead payload to IDM.
 * @param {Object} insiderProps - insider props
 */
export const insiderActions = async insiderProps => {
  const { createOrUpdateIdmData, profile, modelLinkCode, personalInfo, leadIds, leadTargets, financing, edmundsDeals } =
    insiderProps;
  if (leadIds.length === 0) {
    return;
  }

  const { isAuthenticated, isAnonymousUser } = profile;

  const identifierEmail = get(profile, 'data.idm.identifiers.email[0]');
  const isAnonymousUserWithoutEmail = isAnonymousUser && !identifierEmail;
  // this flag will determine the payload type AND which API to use.
  // --- we create a anonymous account for users without a firebase object.
  // --- We want to sign out if the stored profile email and lead email are different.
  const fullPayloadAndNonDeltaAPI =
    !isAuthenticated || (isAnonymousUser && identifierEmail && identifierEmail !== personalInfo.email);

  const leadTargetsInsider = mapLeadTargets(leadTargets, leadIds);

  const insiderPayload = createInsiderPayload(
    leadTargetsInsider,
    personalInfo,
    modelLinkCode,
    fullPayloadAndNonDeltaAPI,
    isAnonymousUserWithoutEmail
  );

  const idmDataToSave = { ...insiderPayload, financing, edmundsDeals };
  await createOrUpdateIdmData({ idmDataToSave, signInAnonymouslyTracking });
};
